import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { Component, OnInit, OnDestroy } from '@angular/core'
import { MessageService } from 'primeng/api'

import { CommunicationService } from '../../data/services/view/communication.service'

@Component({
  selector: 'app-email-preview',
  templateUrl: './email-preview.component.html',
  styleUrls: ['./email-preview.component.scss'],
  providers: [MessageService],
})
export class EmailPreviewComponent implements OnInit, OnDestroy {
  constructor(
    public communicationService: CommunicationService,
    private sanitizer: DomSanitizer,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {}

  get visible(): boolean {
    return this.communicationService.showPreview
  }

  set visible(value: boolean) {
    this.communicationService.showPreview = value
  }

  get htmlContent(): string {
    return this.communicationService.previewHtml
  }

  get email() {
    return this.communicationService.selectedEmail
  }

  /**
   * Verifica si el email tiene bodyHtml disponible
   * Emails anteriores al 23/10/2025 no tendrán este campo
   */
  get hasBodyHtml(): boolean {
    return !!this.htmlContent && this.htmlContent.length > 0
  }

  /**
   * Obtiene mensaje para emails sin bodyHtml
   */
  get noPreviewMessage(): string {
    return (
      '<div style="text-align: center; padding: 40px; color: #666;">' +
      '<i class="pi pi-eye-slash" style="font-size: 3rem; color: #ccc; margin-bottom: 20px;"></i>' +
      '<h3>Preview no disponible</h3>' +
      '<p>Este email fue enviado antes del 23 de octubre de 2025.</p>' +
      '<p>El contenido HTML no está disponible para emails anteriores a esta fecha.</p>' +
      '</div>'
    )
  }

  /**
   * HTML del correo, para pintarlo DENTRO del marco aislado de la plantilla.
   *
   * //! El comentario anterior decía «solo debe usarse con contenido del backend confiable». El
   * //! contenido viene del backend, sí, pero NO es confiable: `renderTemplate` interpola las
   * //! variables con `String(valor)` sin escapar, y esas variables llevan datos del cliente. Un
   * //! nombre preparado se guardaba en `EmailSent.bodyHtml` y se ejecutaba aquí.
   *
   * //* El bypass sigue, y ahora sí es seguro, porque lo que protege NO es el saneador: es el
   * //* `sandbox` del iframe. Sin `allow-scripts` nada de ahí dentro se ejecuta. Se conserva el HTML
   * //* íntegro a propósito — una vista previa que quita etiquetas deja de ser una vista previa.
   *
   * //? La causa raíz sigue viva en la api: `renderTemplate` debería escapar. Mientras no lo haga,
   * //? ese HTML va también en el correo que recibe el cliente. Los clientes de correo no ejecutan
   * //? guiones, así que el panel era el extremo explotable — pero conviene cerrarlo en el origen.
   */
  getSanitizedHtml(): SafeHtml {
    if (!this.hasBodyHtml) return this.sanitizer.bypassSecurityTrustHtml(this.noPreviewMessage)
    return this.communicationService.bypassSecurityTrustHtml(this.htmlContent)
  }

  /**
   * Cierra el dialog de preview
   */
  close() {
    this.visible = false
  }

  /**
   * Copia el HTML al portapapeles
   */
  copyHtml() {
    if (!this.hasBodyHtml) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'No hay HTML para copiar. Este email no tiene contenido disponible.',
      })
      return
    }

    navigator.clipboard
      .writeText(this.htmlContent)
      .then(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'HTML copiado al portapapeles',
        })
      })
      .catch(() => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo copiar el HTML',
        })
      })
  }

  /**
   * Descarga el HTML como archivo
   */
  downloadHtml() {
    if (!this.hasBodyHtml) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'No hay HTML para descargar. Este email no tiene contenido disponible.',
      })
      return
    }

    const blob = new Blob([this.htmlContent], { type: 'text/html' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `email-${this.email?._id || 'preview'}.html`
    a.click()
    window.URL.revokeObjectURL(url)

    this.messageService.add({
      severity: 'success',
      summary: 'Éxito',
      detail: 'HTML descargado correctamente',
    })
  }
}
